import * as React from 'react';
import { Badge, type BadgeProps } from '../ui/badge';
import type { Tone } from '../../theme/colors';

export interface StatusVariant {
  /** Display label. Falls back to the raw status key. */
  label?: React.ReactNode;
  /** Tone applied to the badge. Defaults to neutral. */
  tone?: Tone;
  /** Render as a solid (filled) badge instead of the soft default. */
  solid?: boolean;
}

export type StatusVariantMap<K extends string = string> = Record<K, StatusVariant>;

export interface StatusBadgeProps
  extends Omit<BadgeProps, 'children' | 'tone' | 'solid'> {
  /** The raw status key (e.g. "in_stock", "draft", "active"). */
  status: string;
  /** Optional label override — takes precedence over the variant map. */
  label?: React.ReactNode;
  /** Variant dictionary supplied by the consumer app (domain-specific). */
  variants?: StatusVariantMap;
  /** Fallback tone when no variant matches. */
  fallbackTone?: Tone;
}

/**
 * StatusBadge — generic status pill driven by a consumer-supplied dictionary.
 *
 * The kit deliberately ships *no* default status mapping — every app's
 * lifecycle (orders, tasks, tickets, etc.) is different. The consumer
 * defines a `variants` map and the kit handles the visual treatment.
 *
 * Example (in a consumer app):
 *   const orderStatusVariants: StatusVariantMap = {
 *     pending:   { label: 'Pending',   tone: 'warning' },
 *     paid:      { label: 'Paid',      tone: 'success' },
 *     cancelled: { label: 'Cancelled', tone: 'destructive' },
 *   };
 *   <StatusBadge status={order.status} variants={orderStatusVariants} />
 */
export function StatusBadge({
  status,
  label,
  variants,
  fallbackTone = 'neutral',
  className,
  ...props
}: StatusBadgeProps) {
  const variant = variants?.[status];
  const tone = variant?.tone ?? fallbackTone;
  const solid = variant?.solid ?? false;
  const displayLabel = label ?? variant?.label ?? humanize(status);
  return (
    <Badge tone={tone} solid={solid} className={className} {...props}>
      {displayLabel}
    </Badge>
  );
}

function humanize(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
